import {
  AfterViewInit,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import Chart from 'chart.js/auto';

export interface LumSunDayRow {
  /** -1=ayer, 0=hoy, 1=mañana (respecto al reloj del dispositivo). */
  slot: number;
  date: Date;
  sunriseMin: number | null;
  sunsetMin: number | null;
  sunriseStr: string;
  sunsetStr: string;
}

interface TimeParts {
  hour: number;
  minute: number;
}

interface DaySunTimes {
  sunriseMin: number | null;
  sunsetMin: number | null;
  sunriseStr: string;
  sunsetStr: string;
}

@Component({
  selector: 'app-scheduler-lum',
  imports: [CommonModule, TranslateModule],
  templateUrl: './scheduler-lum.component.html',
  styleUrl: './scheduler-lum.component.scss',
})
export class SchedulerLumComponent implements AfterViewInit, OnChanges, OnDestroy {

  /** Reloj del dispositivo (módulo rtc, level stat). */
  @Input() rtcData: any = null;
  /** Orto/ocaso calculados: ayer, hoy y mañana. */
  @Input() sunTimesData: LumSunDayRow[] = [];
  /** Calendario astronómico: orto/ocaso de hoy desde el dispositivo. */
  @Input() astcalData: any = null;
  /** Zona horaria IANA del dispositivo (tz-lookup). */
  @Input() timeZone = '';
  /** Programación del dispositivo. */
  @Input() scheduleData: any = null;
  @Input() chartIdPrefix = 'chart-lum';

  private chart: Chart | null = null;
  private viewReady = false;
  private nowTimerId: number | null = null;

  private deviceClockBaseDate: Date | null = null;
  private deviceClockCapturedAtMs: number | null = null;

  private readonly minutesPerDay = 1440;
  /** Hoy ocupa el slot 1 en la línea temporal extendida (0=ayer, 1=hoy, 2=mañana). */
  private readonly todaySlot = 1;
  private readonly windowBackMinutes = 6 * 60;
  private readonly windowForwardMinutes = 24 * 60;
  private readonly nowSnapMinutes = 10;
  private readonly chartTopPadding = 72;
  private readonly astroBubbleHalfHeight = 11;
  private readonly astroBubbleArrow = 7;
  private readonly separationNowAstro = 8;

  sunriseLabel = '--:--';
  sunsetLabel = '--:--';

  schedules: any[] = [];
  periods: any[] = [];

  get hasAstcalData(): boolean {
    return this.astcalData != null;
  }

  get sunriseCorrectionMinutes(): number {
    return this.resolveSunCorrectionMinutes('sunrise');
  }

  get sunsetCorrectionMinutes(): number {
    return this.resolveSunCorrectionMinutes('sunset');
  }

  formatSunCorrection(minutes: number): string {
    const value = Math.trunc(Number(minutes) || 0);
    if (value > 0) {
      return `+ ${value}`;
    }
    if (value < 0) {
      return `${value}`;
    }
    return '0';
  }

  /** Corrección astcal en cfg: la API envía minutos (puede ser negativa). */
  private readCfgCorrectionMinutes(kind: 'sunrise' | 'sunset'): number | null {
    const cfg = this.astcalData?.cfg ?? {};
    const keys = kind === 'sunrise'
      ? (['dawn_corr', 'sunrise_corr'] as const)
      : (['dusk_corr', 'sunset_corr'] as const);

    for (const key of keys) {
      const raw = cfg[key];
      if (raw != null && raw !== '') {
        const minutes = Number(raw);
        if (Number.isFinite(minutes)) {
          return Math.round(minutes);
        }
      }
    }
    return null;
  }

  private resolveSunCorrectionMinutes(kind: 'sunrise' | 'sunset'): number {
    const stat = this.astcalData?.stat ?? {};
    const cfgMinutes = this.readCfgCorrectionMinutes(kind);
    if (cfgMinutes != null) {
      return cfgMinutes;
    }

    const base = Number(kind === 'sunrise' ? stat.sunrise : stat.sunset);
    const corrected = Number(
      kind === 'sunrise'
        ? (stat.sunrise_with_correction ?? stat.sunrise)
        : (stat.sunset_with_correction ?? stat.sunset),
    );
    if (Number.isFinite(base) && Number.isFinite(corrected)) {
      return Math.round((corrected - base) / 60);
    }

    return 0;
  }

  ngAfterViewInit(): void {
    this.viewReady = true;
    this.syncDeviceClockReference();
    this.refreshSunLabels();
    this.buildChart();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['scheduleData']) {
      this.schedules = this.scheduleData?.schedules ?? [];
      this.periods = this.scheduleData?.periods ?? [];
      console.log('schedules', this.schedules);
      console.log('periods', this.periods);
    }
    if (changes['rtcData']) {
      this.syncDeviceClockReference();
    }
    if (changes['rtcData'] || changes['astcalData'] || changes['sunTimesData'] || changes['timeZone']) {
      this.refreshSunLabels();
    }
    if (this.viewReady && (changes['rtcData'] || changes['astcalData'] || changes['sunTimesData'] || changes['timeZone'] || changes['scheduleData'])) {
      this.buildChart();
    }
  }

  ngOnDestroy(): void {
    this.clearNowTimer();
    this.chart?.destroy();
    this.chart = null;
  }

  private getRtcStatus(): any {
    return this.rtcData?.status ?? this.rtcData?.stat ?? this.rtcData ?? null;
  }

  private syncDeviceClockReference(): void {
    const status = this.getRtcStatus();
    const ts = Number(status?.local_timestamp);
    if (Number.isFinite(ts) && ts > 0) {
      this.deviceClockBaseDate = new Date(ts * 1000);
      this.deviceClockCapturedAtMs = Date.now();
      return;
    }

    const parsed = this.parseRtcLocalDatetime(status?.local_datetime);
    if (parsed) {
      this.deviceClockBaseDate = parsed;
      this.deviceClockCapturedAtMs = Date.now();
      return;
    }

    this.deviceClockBaseDate = null;
    this.deviceClockCapturedAtMs = null;
  }

  private parseRtcLocalDatetime(value: unknown): Date | null {
    if (typeof value !== 'string' || !value) {
      return null;
    }

    const match = value.match(/^(\d{2}):(\d{2}):(\d{2})(\d{4}-\d{2}-\d{2})$/);
    if (!match) {
      return null;
    }

    const [, hh, mm, ss, datePart] = match;
    const [year, month, day] = datePart.split('-').map(Number);
    const hour = Number(hh);
    const minute = Number(mm);
    const second = Number(ss);

    if ([year, month, day, hour, minute, second].some((n) => !Number.isFinite(n))) {
      return null;
    }

    return new Date(year, month - 1, day, hour, minute, second, 0);
  }

  private getReferenceNow(): Date {
    if (this.deviceClockBaseDate && this.deviceClockCapturedAtMs != null) {
      const elapsedMs = Math.max(0, Date.now() - this.deviceClockCapturedAtMs);
      return new Date(this.deviceClockBaseDate.getTime() + elapsedMs);
    }
    return new Date();
  }

  private getTimeParts(date: Date): TimeParts {
    if (this.timeZone) {
      try {
        const dtf = new Intl.DateTimeFormat('en-US', {
          timeZone: this.timeZone,
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
        });
        const values: Record<string, string> = {};
        for (const part of dtf.formatToParts(date)) {
          if (part.type !== 'literal') {
            values[part.type] = part.value;
          }
        }
        return {
          hour: Number(values.hour),
          minute: Number(values.minute),
        };
      } catch {
        // fallback local
      }
    }

    return {
      hour: date.getHours(),
      minute: date.getMinutes(),
    };
  }

  private formatTimeParts(parts: TimeParts): string {
    return `${String(parts.hour).padStart(2, '0')}:${String(parts.minute).padStart(2, '0')}`;
  }

  private getCurrentTimelineMinute(now = this.getReferenceNow()): number {
    const parts = this.getTimeParts(now);
    return this.todaySlot * this.minutesPerDay + parts.hour * 60 + parts.minute;
  }

  private unixToMinuteOfDay(unixSec: unknown): number | null {
    const ts = Number(unixSec);
    if (!Number.isFinite(ts) || ts <= 0) {
      return null;
    }
    const parts = this.getTimeParts(new Date(ts * 1000));
    return parts.hour * 60 + parts.minute;
  }

  private getSunTimesForChartSlot(chartSlot: number): DaySunTimes {
    const daySlot = chartSlot - this.todaySlot;
    const row = this.sunTimesData.find((item) => item.slot === daySlot);

    if (daySlot === 0 && this.astcalData?.stat) {
      const sunriseMin = this.unixToMinuteOfDay(
        this.astcalData.stat.sunrise_with_correction ?? this.astcalData.stat.sunrise,
      );
      const sunsetMin = this.unixToMinuteOfDay(
        this.astcalData.stat.sunset_with_correction ?? this.astcalData.stat.sunset,
      );
      if (sunriseMin != null && sunsetMin != null) {
        return {
          sunriseMin,
          sunsetMin,
          sunriseStr: this.formatTimeParts({ hour: Math.floor(sunriseMin / 60), minute: sunriseMin % 60 }),
          sunsetStr: this.formatTimeParts({ hour: Math.floor(sunsetMin / 60), minute: sunsetMin % 60 }),
        };
      }
    }

    if (!row) {
      return { sunriseMin: null, sunsetMin: null, sunriseStr: '--:--', sunsetStr: '--:--' };
    }

    return {
      sunriseMin: row.sunriseMin,
      sunsetMin: row.sunsetMin,
      sunriseStr: row.sunriseStr,
      sunsetStr: row.sunsetStr,
    };
  }

  private getSunEventsInWindow(minX: number, maxX: number): Array<{
    timelineMinute: number;
    emoji: string;
    lineColor: string;
    bubbleColor: string;
    label: string;
  }> {
    const events: Array<{
      timelineMinute: number;
      emoji: string;
      lineColor: string;
      bubbleColor: string;
      label: string;
    }> = [];

    for (let chartSlot = 0; chartSlot <= 2; chartSlot++) {
      const dayStart = chartSlot * this.minutesPerDay;
      const sun = this.getSunTimesForChartSlot(chartSlot);

      if (sun.sunriseMin != null) {
        const timelineMinute = dayStart + sun.sunriseMin;
        if (timelineMinute >= minX && timelineMinute <= maxX) {
          events.push({
            timelineMinute,
            emoji: '☀️',
            lineColor: 'rgba(247, 210, 26, 0.85)',
            bubbleColor: 'rgba(247, 210, 26, 0.95)',
            label: sun.sunriseStr,
          });
        }
      }

      if (sun.sunsetMin != null) {
        const timelineMinute = dayStart + sun.sunsetMin;
        if (timelineMinute >= minX && timelineMinute <= maxX) {
          events.push({
            timelineMinute,
            emoji: '🌙',
            lineColor: 'rgba(108, 117, 125, 0.85)',
            bubbleColor: 'rgba(108, 117, 125, 0.95)',
            label: sun.sunsetStr,
          });
        }
      }
    }

    return events;
  }

  private refreshSunLabels(): void {
    const today = this.getSunTimesForChartSlot(this.todaySlot);
    this.sunriseLabel = today.sunriseStr;
    this.sunsetLabel = today.sunsetStr;
  }

  private getWindowRangeAroundNow(nowMinute = this.getCurrentTimelineMinute()): { min: number; max: number } {
    const extendedMin = 0;
    const extendedMax = (this.minutesPerDay * 3) - 1;

    const snap = Math.max(1, Math.trunc(this.nowSnapMinutes));
    const snappedNow = Math.floor(nowMinute / snap) * snap;

    const minRaw = Math.max(extendedMin, snappedNow - this.windowBackMinutes);
    const maxRaw = Math.min(extendedMax, snappedNow + this.windowForwardMinutes);

    const step = 120;
    const min = Math.max(extendedMin, Math.floor(minRaw / step) * step);
    const max = Math.min(extendedMax, Math.ceil(maxRaw / step) * step);

    return { min, max: Math.max(min + 1, max) };
  }

  private formatHourTick(timelineMinute: number): string {
    const minuteOfDay = ((Math.trunc(timelineMinute) % this.minutesPerDay) + this.minutesPerDay) % this.minutesPerDay;
    const hour = Math.floor(minuteOfDay / 60);
    if (hour === 0) {
      return '🕛';
    }
    return `${String(hour).padStart(2, '0')}:00`;
  }

  private getOverlayLayout(chartArea: { top: number; bottom: number }): {
    astroBubbleCenterY: number;
    nowBubbleCenterY: number;
  } {
    const astroBubbleCenterY = Math.max(
      this.astroBubbleHalfHeight + 4,
      chartArea.top - 12 - this.astroBubbleHalfHeight - this.astroBubbleArrow,
    );
    const bubbleStack = this.astroBubbleHalfHeight + this.astroBubbleArrow + this.astroBubbleHalfHeight;
    const nowBubbleCenterY = Math.max(
      this.astroBubbleHalfHeight + 4,
      astroBubbleCenterY - this.separationNowAstro - bubbleStack,
    );

    return { astroBubbleCenterY, nowBubbleCenterY };
  }

  private getBubbleArrowTipY(centerY: number): number {
    return centerY + this.astroBubbleHalfHeight + this.astroBubbleArrow;
  }

  private drawGuideLines(chartRef: Chart): void {
    const chartCtx = chartRef.ctx;
    const chartArea = chartRef.chartArea;
    const xScale = chartRef.scales.x;
    if (!chartCtx || !chartArea || !xScale) {
      return;
    }

    const layout = this.getOverlayLayout(chartArea);
    const minX = typeof xScale.min === 'number' ? xScale.min : 0;
    const maxX = typeof xScale.max === 'number' ? xScale.max : this.minutesPerDay * 3;

    const specs: Array<{ timelineMinute: number; color: string; dash: number[]; alpha: number; kind: 'astro' | 'now' }> = [];

    for (const event of this.getSunEventsInWindow(minX, maxX)) {
      specs.push({
        timelineMinute: event.timelineMinute,
        color: event.lineColor,
        dash: [5, 5],
        alpha: 1,
        kind: 'astro',
      });
    }

    const nowMinute = this.getCurrentTimelineMinute();
    if (nowMinute >= minX && nowMinute <= maxX) {
      specs.push({
        timelineMinute: nowMinute,
        color: '#4267B2',
        dash: [4, 6],
        alpha: 0.85,
        kind: 'now',
      });
    }

    for (const spec of specs) {
      const bubbleCenterY = spec.kind === 'now' ? layout.nowBubbleCenterY : layout.astroBubbleCenterY;
      const lineStartY = this.getBubbleArrowTipY(bubbleCenterY);
      const x = xScale.getPixelForValue(spec.timelineMinute);
      if (!Number.isFinite(x) || chartArea.bottom <= lineStartY) {
        continue;
      }

      chartCtx.save();
      chartCtx.strokeStyle = spec.color;
      chartCtx.globalAlpha = spec.alpha;
      chartCtx.lineWidth = 2;
      chartCtx.setLineDash(spec.dash);
      chartCtx.beginPath();
      chartCtx.moveTo(x, lineStartY);
      chartCtx.lineTo(x, chartArea.bottom);
      chartCtx.stroke();
      chartCtx.setLineDash([]);
      chartCtx.restore();
    }
  }

  private drawBubble(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    text: string,
    bgColor: string,
    textColor: string,
    arrowSize = 7,
  ): void {
    ctx.save();
    ctx.font = '600 11px sans-serif';
    const paddingX = 8;
    const bubbleWidth = ctx.measureText(text).width + paddingX * 2;
    const bubbleHeight = 22;
    const radius = 8;
    const left = x - bubbleWidth / 2;
    const top = y - bubbleHeight / 2;

    ctx.fillStyle = bgColor;
    ctx.beginPath();
    ctx.moveTo(left + radius, top);
    ctx.lineTo(left + bubbleWidth - radius, top);
    ctx.quadraticCurveTo(left + bubbleWidth, top, left + bubbleWidth, top + radius);
    ctx.lineTo(left + bubbleWidth, top + bubbleHeight - radius);
    ctx.quadraticCurveTo(left + bubbleWidth, top + bubbleHeight, left + bubbleWidth - radius, top + bubbleHeight);
    ctx.lineTo(x + arrowSize, top + bubbleHeight);
    ctx.lineTo(x, top + bubbleHeight + arrowSize);
    ctx.lineTo(x - arrowSize, top + bubbleHeight);
    ctx.lineTo(left + radius, top + bubbleHeight);
    ctx.quadraticCurveTo(left, top + bubbleHeight, left, top + bubbleHeight - radius);
    ctx.lineTo(left, top + radius);
    ctx.quadraticCurveTo(left, top, left + radius, top);
    ctx.closePath();
    ctx.fill();

    ctx.fillStyle = textColor;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(text, x, y);
    ctx.restore();
  }

  private buildChart(): void {
    this.clearNowTimer();

    const canvas = document.getElementById(`${this.chartIdPrefix}-0`) as HTMLCanvasElement | null;
    if (!canvas) {
      return;
    }

    this.chart?.destroy();
    this.chart = null;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return;
    }

    const range = this.getWindowRangeAroundNow();
    const totalMinutes = this.minutesPerDay * 3;

    const chart = new Chart(ctx, {
      type: 'line',
      data: {
        datasets: [{
          data: [{ x: 0, y: 0 }, { x: totalMinutes, y: 0 }],
          borderWidth: 0,
          pointRadius: 0,
          fill: false,
        }],
      },
      options: {
        animation: false,
        responsive: true,
        maintainAspectRatio: false,
        layout: {
          padding: { top: this.chartTopPadding },
        },
        scales: {
          y: {
            display: false,
            min: 0,
            max: 1,
          },
          x: {
            type: 'linear',
            min: range.min,
            max: range.max,
            ticks: {
              autoSkip: false,
              stepSize: 120,
              maxRotation: 0,
              font: { size: 10 },
              callback: (value) => {
                const x = Math.trunc(Number(value));
                if (!Number.isFinite(x)) {
                  return '';
                }
                return this.formatHourTick(x);
              },
            },
          },
        },
        plugins: {
          legend: { display: false },
          tooltip: { enabled: false },
        },
      },
      plugins: [{
        id: 'lumNightBackground',
        beforeDatasetsDraw: (chartInstance) => {
          const chartRef = chartInstance as Chart;
          const chartCtx = chartRef.ctx;
          const chartArea = chartRef.chartArea;
          const xScale = chartRef.scales.x;
          if (!chartArea || !xScale) {
            return;
          }

          const minX = typeof xScale.min === 'number' ? xScale.min : 0;
          const maxX = typeof xScale.max === 'number' ? xScale.max : this.minutesPerDay * 3;

          const drawNight = (fromX: number, toX: number) => {
            const x1 = Math.max(chartArea.left, xScale.getPixelForValue(fromX));
            const x2 = Math.min(chartArea.right, xScale.getPixelForValue(toX));
            if (!Number.isFinite(x1) || !Number.isFinite(x2) || x2 <= x1) {
              return;
            }
            chartCtx.save();
            chartCtx.fillStyle = 'rgba(1, 30, 65, 0.08)';
            chartCtx.fillRect(x1, chartArea.top, x2 - x1, chartArea.bottom - chartArea.top);
            chartCtx.restore();
          };

          for (let chartSlot = 0; chartSlot <= 2; chartSlot++) {
            const dayStart = chartSlot * this.minutesPerDay;
            const dayEnd = dayStart + this.minutesPerDay;
            const sun = this.getSunTimesForChartSlot(chartSlot);
            if (sun.sunriseMin == null || sun.sunsetMin == null) {
              continue;
            }

            const sunriseMinute = dayStart + sun.sunriseMin;
            const sunsetMinute = dayStart + sun.sunsetMin;
            const visibleStart = Math.max(minX, dayStart);
            const visibleEnd = Math.min(maxX, dayEnd);

            if (visibleStart < sunriseMinute) {
              drawNight(visibleStart, Math.min(sunriseMinute, visibleEnd));
            }
            if (sunsetMinute < visibleEnd) {
              drawNight(Math.max(sunsetMinute, visibleStart), visibleEnd);
            }
          }
        },
      }, {
        id: 'lumGuideLines',
        afterDraw: (chartInstance) => {
          this.drawGuideLines(chartInstance as Chart);
        },
      }, {
        id: 'lumSunMoonMarkers',
        afterDraw: (chartInstance) => {
          const chartRef = chartInstance as Chart;
          const chartCtx = chartRef.ctx;
          const chartArea = chartRef.chartArea;
          const xScale = chartRef.scales.x;
          if (!chartArea || !xScale) {
            return;
          }

          const layout = this.getOverlayLayout(chartArea);
          const minX = typeof xScale.min === 'number' ? xScale.min : 0;
          const maxX = typeof xScale.max === 'number' ? xScale.max : this.minutesPerDay * 3;

          for (const event of this.getSunEventsInWindow(minX, maxX)) {
            const x = xScale.getPixelForValue(event.timelineMinute);
            if (!Number.isFinite(x)) {
              continue;
            }

            this.drawBubble(
              chartCtx,
              x,
              layout.astroBubbleCenterY,
              `${event.emoji} ${event.label}`,
              event.bubbleColor,
              '#fff',
            );
          }
        },
      }, {
        id: 'lumNowMarker',
        afterDraw: (chartInstance) => {
          const chartRef = chartInstance as Chart;
          const chartCtx = chartRef.ctx;
          const chartArea = chartRef.chartArea;
          const xScale = chartRef.scales.x;
          if (!chartArea || !xScale) {
            return;
          }

          const nowMinute = this.getCurrentTimelineMinute();
          const minX = typeof xScale.min === 'number' ? xScale.min : 0;
          const maxX = typeof xScale.max === 'number' ? xScale.max : this.minutesPerDay * 3;
          if (nowMinute < minX || nowMinute > maxX) {
            return;
          }

          const x = xScale.getPixelForValue(nowMinute);
          if (!Number.isFinite(x)) {
            return;
          }

          const nowLabel = this.formatTimeParts(this.getTimeParts(this.getReferenceNow()));
          const layout = this.getOverlayLayout(chartArea);
          this.drawBubble(chartCtx, x, layout.nowBubbleCenterY, `🕒 ${nowLabel}`, '#4267B2', '#fff');
        },
      }],
    });

    this.chart = chart;

    this.nowTimerId = window.setInterval(() => {
      if (!this.chart) {
        return;
      }
      const nextRange = this.getWindowRangeAroundNow();
      const xScale = (this.chart.options.scales as any)?.x;
      if (xScale) {
        xScale.min = nextRange.min;
        xScale.max = nextRange.max;
      }
      this.chart.update('none');
    }, 30_000);
  }

  private clearNowTimer(): void {
    if (this.nowTimerId != null) {
      window.clearInterval(this.nowTimerId);
      this.nowTimerId = null;
    }
  }
}
